import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { shopperHeaders } from '@/lib/shopper';

const WishlistContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * المفضلة على مستوى التركيبة (لون + خصائص): كل عنصر = { id, variant_id, options_label, product }.
 */
export const WishlistProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token, loading: authLoading } = useAuth();

  const fetchWishlist = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/wishlist`, { headers: shopperHeaders(token) });
      setItems(response.data?.data || []);
    } catch (error) {
      console.error('Failed to fetch wishlist:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // يعاد التحميل عند تسجيل الدخول/الخروج (مفضلة الزائر بتتدمج في الحساب من الباك إند)
  useEffect(() => {
    if (!authLoading) fetchWishlist();
  }, [authLoading, fetchWishlist]);

  const variantIds = useMemo(() => new Set(items.map((item) => item.variant_id)), [items]);

  const isInWishlist = (variantId) => Boolean(variantId) && variantIds.has(variantId);

  const setMembership = (product, variantId, inWishlist, optionsLabel) => {
    setItems((current) => {
      const rest = current.filter((item) => item.variant_id !== variantId);
      return inWishlist
        ? [{ id: `tmp-${variantId}`, variant_id: variantId, options_label: optionsLabel, product }, ...rest]
        : rest;
    });
  };

  /**
   * يضيف التركيبة المختارة أو يشيلها (تحديث فوري للواجهة)، ويرجع true لو اتضافت.
   * optionsLabel: وصف التركيبة للعرض (مثلاً "أسود / 160-170 سم").
   */
  const toggleWishlist = async (product, variantId, optionsLabel = '') => {
    const wasIn = variantIds.has(variantId);
    setMembership(product, variantId, !wasIn, optionsLabel);
    try {
      const response = await axios.post(
        `${API}/wishlist/toggle`,
        { variant_id: variantId },
        { headers: shopperHeaders(token) }
      );
      const inWishlist = response.data?.data?.in_wishlist;
      if (inWishlist === wasIn) setMembership(product, variantId, inWishlist, optionsLabel);
      // العنصر المضاف محتاج بيانات التركيبة كاملة (السعر والخصائص) لصفحة المفضلة
      if (inWishlist) fetchWishlist();
      return inWishlist;
    } catch (error) {
      setMembership(product, variantId, wasIn, optionsLabel);
      throw error;
    }
  };

  return (
    <WishlistContext.Provider
      value={{ items, wishlistCount: items.length, loading, isInWishlist, toggleWishlist, fetchWishlist }}
    >
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within WishlistProvider');
  }
  return context;
};
